import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useProduct, useProducts } from '../../hooks/useFirestore'
import { useCategories } from '../../hooks/useCategories'
import { useCart } from '../../hooks/useCart'
import { useBusinessRulesContext } from '../../hooks/useBusinessRulesContext'
import { getHelpSettings } from '../../services/helpSettings.service'
import { getImageUrl } from '../../services/cloudinary.service'
import { getProductStockSummary } from '../../services/stock.service'
import { formatCurrency } from '../../utils/helpers'
import { buildWhatsAppLinkAR } from '../../utils/whatsapp'
import { getStockForSelection, normalizeStockColor } from '../../utils/stock'
import Toast from '../../components/Toast'

const buildEmbedUrl = (url) => {
  const raw = String(url || '').trim()
  if (!raw) return null
  if (raw.includes('youtube.com/embed/')) return raw
  const ytMatch = raw.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]+)/)
  if (ytMatch?.[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`
  const igMatch = raw.match(/instagram\.com\/(reel|p|tv)\/([A-Za-z0-9_-]+)/)
  if (igMatch?.[1] && igMatch?.[2]) return `https://www.instagram.com/${igMatch[1]}/${igMatch[2]}/embed`
  const ttMatch = raw.match(/tiktok\.com\/.+\/video\/(\d+)/)
  if (ttMatch?.[1]) return `https://www.tiktok.com/embed/v2/${ttMatch[1]}`
  if (raw.includes('facebook.com')) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(raw)}&show_text=false&width=500`
  }
  return null
}

const resolveImageSource = (image) => {
  if (!image) return null
  if (typeof image === 'string') return image
  if (typeof image === 'object') return image.fileId || image.url || image.thumbnailUrl || null
  return null
}

const getVariantImages = (product, transformations) => {
  const images = []
  const variants = Array.isArray(product?.variants) ? product.variants : []
  variants.forEach(variant => {
    const variantImages = Array.isArray(variant?.images) ? variant.images : []
    variantImages.forEach(image => {
      const source = resolveImageSource(image)
      if (!source) return
      const url = getImageUrl(source, transformations)
      if (!images.includes(url)) images.push(url)
    })
  })
  if (images.length === 0) {
    const fallbackImages = Array.isArray(product?.images) ? product.images : []
    fallbackImages.forEach(image => {
      const source = resolveImageSource(image)
      if (!source) return
      const url = getImageUrl(source, transformations)
      if (!images.includes(url)) images.push(url)
    })
  }
  return images
}

const isLightColor = (value) => {
  const hex = normalizeStockColor(value)
  if (!hex) return false
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
  return luminance > 0.9
}

const parseColors = (product) => {
  const colors = []
  const colorNames = product?.colorNames && typeof product.colorNames === 'object' ? product.colorNames : {}
  const addColor = (value, fallbackLabel) => {
    const normalized = normalizeStockColor(value)
    if (!normalized) return
    const labelRaw = colorNames[normalized] || colorNames[value] || fallbackLabel || normalized
    const label = typeof labelRaw === 'string' ? labelRaw.trim() : String(labelRaw || '').trim()
    colors.push({ label: label || normalized, value: normalized })
  }
  if (typeof product?.color === 'string' && product.color.startsWith('#')) {
    const primaryLabel = colorNames[product.color] || product?.colorName || 'Principal'
    addColor(product.color, primaryLabel)
  }
  if (Array.isArray(product?.colors)) {
    product.colors.forEach((c) => addColor(c, colorNames[c]))
  }
  const variantColors = (product?.variants || []).filter(v => v?.type === 'color')
  variantColors.forEach((v) => {
    if (typeof v?.name === 'string' && v.name.startsWith('#')) {
      addColor(v.name, v.colorName || colorNames[v.name])
    }
  })
  const unique = new Map()
  colors.forEach(c => unique.set(c.value, c))
  return Array.from(unique.values())
}

const parseSizes = (product) => {
  const sizes = []
  if (Array.isArray(product?.sizes)) {
    product.sizes.forEach(s => {
      if (typeof s === 'string' && s.trim()) sizes.push(s.trim())
    })
  }
  const variantSizes = (product?.variants || []).filter(v => v?.type === 'size')
  variantSizes.forEach(v => {
    if (typeof v?.name === 'string' && v.name.trim()) sizes.push(v.name.trim())
  })
  return Array.from(new Set(sizes))
}

function FashionProductDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { product, loading } = useProduct(id)
  const { addToCart, getCartItemCount } = useCart()
  const { categories } = useCategories(false)
  const { rules } = useBusinessRulesContext()
  const outOfStockPolicy = rules?.outOfStockPolicy || { visibility: 'show', purchaseBehavior: 'block_purchase', inquiryChannel: 'whatsapp', labelText: 'Sin stock' }

  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [selectedColor, setSelectedColor] = useState(null)
  const [selectedSize, setSelectedSize] = useState(null)
  const [error, setError] = useState(null)
  const [quantity, setQuantity] = useState(1)
  const [notification, setNotification] = useState(null)
  const [stockSummary, setStockSummary] = useState(null)
  const [helpSettings, setHelpSettings] = useState(null)

  const colors = useMemo(() => parseColors(product), [product])
  const sizes = useMemo(() => parseSizes(product), [product])
  const selectedColorLabel = useMemo(() => {
    if (!selectedColor) return null
    const found = colors.find(c => c.value === selectedColor)
    return found?.label || selectedColor
  }, [colors, selectedColor])

  useEffect(() => {
    if (colors.length === 1 && !selectedColor) setSelectedColor(colors[0].value)
  }, [colors, selectedColor])

  useEffect(() => {
    if (sizes.length === 1 && !selectedSize) setSelectedSize(sizes[0])
  }, [sizes, selectedSize])

  useEffect(() => {
    let mounted = true
    const load = async () => {
      if (!product?.id) {
        if (mounted) setStockSummary(null)
        return
      }
      try {
        const summary = await getProductStockSummary(product.id)
        if (mounted) setStockSummary(summary)
      } catch {
        if (mounted) setStockSummary(null)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [product?.id])

  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        const data = await getHelpSettings()
        if (mounted) setHelpSettings(data)
      } catch {
        if (mounted) setHelpSettings(null)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [])

  const hasVariantBreakdown = useMemo(() => {
    const byColor = stockSummary?.byColor || {}
    const bySize = stockSummary?.bySize || {}
    const byColorSize = stockSummary?.byColorSize || {}
    return Object.keys(byColor).length > 0 || Object.keys(bySize).length > 0 || Object.keys(byColorSize).length > 0
  }, [stockSummary])

  const needsColor = colors.length > 0
  const needsSize = sizes.length > 0

  const selectionComplete = useMemo(() => {
    if (needsColor && !selectedColor) return false
    if (needsSize && !selectedSize) return false
    return true
  }, [needsColor, needsSize, selectedColor, selectedSize])

  const variants = useMemo(() => (Array.isArray(product?.variants) ? product.variants : []), [product])

  const selectedVariant = useMemo(() => {
    if (!selectionComplete) return null
    return variants.find((variant) => {
      const selection = variant?.selection || {}
      const colorMatch = selectedColor
        ? normalizeStockColor(selection.color) === normalizeStockColor(selectedColor)
        : true
      const sizeMatch = selectedSize
        ? String(selection.size || '').trim().toUpperCase() === String(selectedSize || '').trim().toUpperCase()
        : true
      return colorMatch && sizeMatch
    }) || null
  }, [selectionComplete, selectedColor, selectedSize, variants])

  const fallbackImages = useMemo(() => {
    const list = product?.images || []
    return list.map((img) => resolveImageSource(img)).filter(Boolean)
  }, [product])

  const allVariantImages = useMemo(() => {
    return variants.flatMap(variant => (variant?.images || []).map(img => resolveImageSource(img))).filter(Boolean)
  }, [variants])

  const selectedVariantImages = useMemo(() => {
    if (!selectedVariant) return []
    return (selectedVariant.images || []).map(img => resolveImageSource(img)).filter(Boolean)
  }, [selectedVariant])

  const images = useMemo(() => {
    if (selectionComplete && selectedVariantImages.length > 0) return selectedVariantImages
    if (allVariantImages.length > 0) return allVariantImages
    return fallbackImages
  }, [allVariantImages, fallbackImages, selectionComplete, selectedVariantImages])

  const allVariantVideos = useMemo(() => {
    return variants.flatMap(variant => (Array.isArray(variant?.videos) ? variant.videos : [])).map(buildEmbedUrl).filter(Boolean)
  }, [variants])

  const selectedVariantVideos = useMemo(() => {
    if (!selectedVariant || !Array.isArray(selectedVariant.videos)) return []
    return selectedVariant.videos.map(buildEmbedUrl).filter(Boolean)
  }, [selectedVariant])

  const videos = useMemo(() => {
    if (selectionComplete && selectedVariantVideos.length > 0) return selectedVariantVideos
    return allVariantVideos
  }, [allVariantVideos, selectionComplete, selectedVariantVideos])

  const media = useMemo(() => {
    const items = []
    images.forEach((src) => items.push({ type: 'image', src }))
    videos.forEach((src) => items.push({ type: 'video', src }))
    return items.length > 0 ? items : [{ type: 'image', src: getImageUrl(null) }]
  }, [images, videos])

  useEffect(() => {
    setActiveImageIndex(0)
  }, [media])

  const selectionStock = useMemo(() => {
    if (!product) return 0
    if (!selectionComplete) return 0
    if (!stockSummary) return product.stock || 0
    if (!hasVariantBreakdown) return stockSummary.total || product.stock || 0
    return getStockForSelection(stockSummary, { color: selectedColor, size: selectedSize })
  }, [hasVariantBreakdown, product, selectedColor, selectedSize, selectionComplete, stockSummary])

  const visibleColors = useMemo(() => {
    if (!needsColor) return []
    if (outOfStockPolicy.visibility !== 'hide') return colors
    if (!stockSummary || !hasVariantBreakdown) return colors
    if (needsSize && selectedSize) {
      return colors.filter(c => getStockForSelection(stockSummary, { color: c.value, size: selectedSize }) > 0)
    }
    return colors.filter(c => getStockForSelection(stockSummary, { color: c.value }) > 0)
  }, [colors, hasVariantBreakdown, needsColor, needsSize, outOfStockPolicy.visibility, selectedSize, stockSummary])

  const visibleSizes = useMemo(() => {
    if (!needsSize) return []
    if (outOfStockPolicy.visibility !== 'hide') return sizes
    if (!stockSummary || !hasVariantBreakdown) return sizes
    if (needsColor && selectedColor) {
      return sizes.filter(s => getStockForSelection(stockSummary, { color: selectedColor, size: s }) > 0)
    }
    return sizes.filter(s => getStockForSelection(stockSummary, { size: s }) > 0)
  }, [hasVariantBreakdown, needsColor, needsSize, outOfStockPolicy.visibility, selectedColor, sizes, stockSummary])

  useEffect(() => {
    if (outOfStockPolicy.visibility !== 'hide') return
    if (!hasVariantBreakdown) return
    if (needsColor && selectedColor && visibleColors.length > 0) {
      if (!visibleColors.some(c => c.value === selectedColor)) {
        setSelectedColor(visibleColors[0].value)
        setError(null)
      }
    }
    if (needsSize && selectedSize && visibleSizes.length > 0) {
      if (!visibleSizes.includes(selectedSize)) {
        setSelectedSize(visibleSizes[0])
        setError(null)
      }
    }
  }, [hasVariantBreakdown, needsColor, needsSize, outOfStockPolicy.visibility, selectedColor, selectedSize, visibleColors, visibleSizes])

  useEffect(() => {
    if (outOfStockPolicy.purchaseBehavior === 'allow_purchase') return
    if (!selectionComplete) return
    const maxQty = Math.max(1, selectionStock)
    setQuantity(prev => Math.min(Math.max(1, prev), maxQty))
  }, [outOfStockPolicy.purchaseBehavior, selectionComplete, selectionStock])

  const categoryName = useMemo(() => {
    if (!product?.categoryId) return null
    const found = (categories || []).find(c => c.id === product.categoryId)
    return found?.name || null
  }, [categories, product?.categoryId])

  const { products: related } = useProducts(
    { categoryId: product?.categoryId, orderByField: 'createdAt', orderDirection: 'desc', limitResults: 8 },
    [product?.categoryId]
  )

  const relatedProducts = useMemo(() => {
    const base = (related || []).filter(p => p?.id !== product?.id && p?.active !== false)
    const filtered = outOfStockPolicy.visibility === 'hide'
      ? base.filter(p => (p?.stock || 0) > 0)
      : base
    return filtered.slice(0, 4)
  }, [outOfStockPolicy.visibility, product?.id, related])

  const hasDiscount = product?.specialPrice > 0 && product?.specialPrice < product?.price
  const displayPrice = hasDiscount ? product?.specialPrice : product?.price

  const buildInquiryUrl = (p) => {
    if (!p) return null
    const whatsapp = helpSettings?.contact?.socialMedia?.whatsapp
    const url = typeof window !== 'undefined' ? window.location.href : ''
    const parts = []
    if (selectedColorLabel) parts.push(`Color: ${selectedColorLabel}`)
    if (selectedSize) parts.push(`Talle: ${selectedSize}`)
    const selectionText = parts.length ? ` (${parts.join(', ')})` : ''
    const text = `Hola! Quiero consultar por el producto: ${p.name}${selectionText}. Link: ${url}`
    return buildWhatsAppLinkAR(whatsapp, text)
  }

  const handleAdd = () => {
    if (!product) return
    if (needsColor && !selectedColor) {
      setError('Selecciona un color para continuar')
      return
    }
    if (needsSize && !selectedSize) {
      setError('Selecciona un talle para continuar')
      return
    }

    setError(null)

    if (outOfStockPolicy.purchaseBehavior !== 'allow_purchase') {
      if (selectionStock <= 0) {
        if (outOfStockPolicy.purchaseBehavior === 'inquiry_only') {
          const href = buildInquiryUrl(product)
          if (href) {
            window.open(href, '_blank', 'noreferrer')
            return
          }
          navigate(outOfStockPolicy.inquiryChannel === 'help_page' ? '/ayuda' : '/ayuda#contacto')
          return
        }
        setError('Este producto no tiene stock disponible')
        return
      }
      if (selectionStock < quantity) {
        setError('No hay suficiente stock disponible')
        return
      }
    }

    const parts = []
    const displayColor = selectedColorLabel || selectedColor
    if (displayColor) parts.push(displayColor)
    if (selectedSize) parts.push(selectedSize)
    const name = parts.join(' | ')
    const sku = selectedVariant?.sku || ''

    const safeQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1
    addToCart(product, {
      type: 'fashion',
      name,
      sku,
      serialNumber: selectedVariant?.serialNumber || '',
      images: selectedVariant?.images || [],
      videos: selectedVariant?.videos || [],
      priceDelta: selectedVariant?.priceDelta ?? (selectedVariant?.price ?? null),
      stock: selectionStock,
      selection: { color: selectedColor, colorLabel: selectedColorLabel || selectedColor, size: selectedSize }
    }, safeQuantity)
    setNotification({
      type: 'success',
      message: `Agregaste ${safeQuantity} × ${product.name} al carrito`,
      duration: 6000,
      action: { label: 'Ver carrito', onClick: () => navigate('/carrito') }
    })
  }

  if (loading) {
    return (
      <main className="w-full max-w-[1440px] mx-auto px-6 lg:px-12 py-12">
        <div className="text-sm text-gray-500">Cargando producto...</div>
      </main>
    )
  }

  if (!product) {
    return (
      <main className="w-full max-w-[1440px] mx-auto px-6 lg:px-12 py-12">
        <div className="text-sm text-gray-500">Producto no encontrado.</div>
      </main>
    )
  }

  const activeMedia = media[activeImageIndex] || media[0] || null
  const activeMediaUrl = activeMedia?.type === 'image'
    ? getImageUrl(activeMedia.src, { width: 1400, height: 1600, crop: 'fill' })
    : activeMedia?.src
  const availableStock = selectionComplete ? selectionStock : (product?.stock || 0)
  const isOutOfStockSelected = selectionComplete && availableStock <= 0
  const buttonDisabled = isOutOfStockSelected && outOfStockPolicy.purchaseBehavior === 'block_purchase'
  const buttonLabel = isOutOfStockSelected && outOfStockPolicy.purchaseBehavior === 'inquiry_only'
    ? 'Consultar'
    : isOutOfStockSelected && outOfStockPolicy.purchaseBehavior !== 'allow_purchase'
      ? (outOfStockPolicy.labelText || 'Sin stock')
      : 'Agregar al carrito'

  return (
    <main className="w-full flex flex-col items-center">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'success'}
        duration={notification?.duration ?? 3000}
        actionLabel={notification?.action?.label}
        onAction={notification?.action?.onClick}
        onClose={() => setNotification(null)}
      />
      <div className="w-full max-w-[1440px] px-6 lg:px-12 py-12">
        <nav className="flex mb-8 text-xs font-medium uppercase tracking-widest text-gray-400 gap-2">
          <Link className="hover:text-[#c27e70]" to="/">Inicio</Link>
          <span>/</span>
          <Link className="hover:text-[#c27e70]" to="/productos">Tienda</Link>
          {categoryName ? (
            <>
              <span>/</span>
              <Link className="hover:text-[#c27e70]" to={`/productos?categoria=${encodeURIComponent(product.categoryId)}`}>{categoryName}</Link>
            </>
          ) : null}
          <span>/</span>
          <span className="text-[#181211]">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex gap-4">
            <div className="hidden md:flex flex-col gap-4 w-24">
              {media.slice(0, 6).map((item, idx) => {
                const active = idx === activeImageIndex
                return (
                  <button
                    key={`${item.type}-${item.src}-${idx}`}
                    type="button"
                    className={`aspect-[3/4] rounded-lg overflow-hidden border-2 ${active ? 'border-[#c27e70]' : 'border-transparent hover:border-[#e8e6e1]'} cursor-pointer`}
                    onClick={() => setActiveImageIndex(idx)}
                    aria-label={`Media ${idx + 1}`}
                  >
                    {item.type === 'video' ? (
                      <div className="w-full h-full bg-gradient-to-br from-[#f9f6f4] to-[#e8e1dc] flex flex-col items-center justify-center gap-2 text-[#3f3430]">
                        <div className="size-10 rounded-full bg-white/90 shadow-sm flex items-center justify-center border border-[#e8e1dc]">
                          <span className="material-symbols-outlined text-[20px]">play_arrow</span>
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.2em]">Video</span>
                      </div>
                    ) : (
                      <img alt={`Miniatura ${idx + 1}`} className="w-full h-full object-cover" src={getImageUrl(item.src, { width: 240, height: 320, crop: 'fill' })} />
                    )}
                  </button>
                )
              })}
            </div>

            <div className="flex-1 rounded-2xl overflow-hidden bg-[#f4f3f1]">
              {activeMedia?.type === 'video' ? (
                <iframe
                  src={activeMediaUrl}
                  title={`Video ${activeImageIndex + 1}`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <img alt={product.name} className="w-full h-full object-cover" src={activeMediaUrl} />
              )}
            </div>
          </div>
          </div>

          <div className="lg:col-span-5 flex flex-col">
            {product.featured ? (
              <div className="mb-2">
                <span className="text-[#c27e70] text-xs font-bold uppercase tracking-widest">Destacado</span>
              </div>
            ) : null}
            <h1 className="text-4xl font-bold mb-4 tracking-tight">{product.name}</h1>
            <div className="flex items-end gap-3 mb-8">
              <p className="text-2xl font-light text-[#181211]/90">{formatCurrency(displayPrice || 0)}</p>
              {hasDiscount ? (
                <p className="text-sm text-gray-400 line-through pb-1">{formatCurrency(product.price || 0)}</p>
              ) : null}
            </div>

            <div className="space-y-8 mb-10">
              {needsColor ? (
                <div className="rounded-2xl border border-[#efe9e6] bg-white p-5 shadow-sm">
                  <p className="text-sm font-bold uppercase tracking-wider mb-4">
                    Color: <span className="font-normal text-gray-500">{selectedColorLabel || 'selecciona'}</span>
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {visibleColors.map((c) => {
                      const isSelected = selectedColor === c.value
                      const isLight = isLightColor(c.value)
                      return (
                        <div key={c.value} className="flex flex-col items-center gap-2 rounded-2xl border border-[#ece7e2] bg-[#f8f6f4] px-3 py-3">
                          <button
                            type="button"
                            className={`w-9 h-9 rounded-full border-2 shadow-sm ${isSelected ? 'border-[#c27e70] ring-2 ring-offset-2 ring-[#c27e70]/30' : isLight ? 'border-[#bcb5af] ring-1 ring-black/10' : 'border-[#e8e6e1]'}`}
                            style={{ backgroundColor: c.value }}
                            onClick={() => { setSelectedColor(c.value); setError(null) }}
                            aria-label={`Color ${c.label}`}
                          />
                          <span className="text-[11px] font-semibold text-gray-600">{c.label}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : null}

              {needsSize ? (
                <div className="rounded-2xl border border-[#efe9e6] bg-white p-5 shadow-sm">
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-sm font-bold uppercase tracking-wider">Talle</p>
                    <a className="text-xs font-semibold underline underline-offset-4 hover:text-[#c27e70] transition-colors uppercase tracking-widest" href="#guia-talles">
                      Guía de Talles
                    </a>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {visibleSizes.slice(0, 10).map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`py-3 text-sm font-semibold border rounded-lg transition-all ${
                          selectedSize === s
                            ? 'border-[#c27e70] bg-[#c27e70] text-white shadow-sm ring-2 ring-[#c27e70]/30'
                            : 'border-[#e8e6e1] bg-white text-[#181211] hover:border-[#c27e70] hover:bg-[#f8f6f4]'
                        }`}
                        onClick={() => { setSelectedSize(s); setError(null) }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {hasVariantBreakdown && (needsColor || needsSize) ? (
                <div className="rounded-2xl border border-[#efe9e6] bg-white p-5 shadow-sm">
                  <p className="text-sm font-bold uppercase tracking-wider mb-4">Stock por variantes</p>
                  {needsColor && needsSize ? (
                    <div className="w-full overflow-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="py-2 pr-4 font-semibold">Talle</th>
                            {colors.map(c => (
                              <th key={c.value} className="py-2 pr-4 font-semibold">
                                <div className="flex items-center gap-2">
                                  <span className="inline-block w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: c.value }} />
                                  <span className="whitespace-nowrap">{c.label}</span>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sizes.map(s => (
                            <tr key={s} className="border-t border-[#f1eeea]">
                              <td className="py-2 pr-4 font-semibold text-[#181211]">{s}</td>
                              {colors.map(c => {
                                const qty = getStockForSelection(stockSummary, { color: c.value, size: s })
                                const muted = qty <= 0
                                return (
                                  <td key={`${s}-${c.value}`} className={`py-2 pr-4 ${muted ? 'text-gray-400' : 'text-[#181211]'}`}>
                                    {qty || 0}
                                  </td>
                                )
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : needsColor ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {colors.map(c => {
                        const qty = getStockForSelection(stockSummary, { color: c.value })
                        return (
                          <div key={c.value} className="flex items-center justify-between rounded-xl border border-[#f1eeea] px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="inline-block w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: c.value }} />
                              <span className="text-sm font-semibold text-[#181211]">{c.label}</span>
                            </div>
                            <span className="text-sm font-semibold text-gray-600">{qty || 0}</span>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {sizes.map(s => {
                        const qty = getStockForSelection(stockSummary, { size: s })
                        return (
                          <div key={s} className="flex items-center justify-between rounded-xl border border-[#f1eeea] px-4 py-3">
                            <span className="text-sm font-semibold text-[#181211]">{s}</span>
                            <span className="text-sm font-semibold text-gray-600">{qty || 0}</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {error ? (
              <div className="mb-4 text-sm font-semibold text-red-600">{error}</div>
            ) : null}

            <div className="flex flex-col gap-4 mb-12">
              <div className="flex items-center justify-between gap-6">
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-gray-500">Cantidad</span>
                  <div className="flex items-center border border-[#e8e6e1] rounded-lg overflow-hidden h-12">
                    <button
                      type="button"
                      className="w-12 h-full flex items-center justify-center text-[#181211] hover:bg-[#f5f3f1] transition-colors disabled:opacity-40"
                      onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                      disabled={quantity <= 1}
                    >
                      <span className="material-symbols-outlined text-[20px]">remove</span>
                    </button>
                    <input
                      type="number"
                      min="1"
                      className="w-16 h-full text-center text-sm font-semibold text-[#181211] border-x border-[#e8e6e1] focus:outline-none"
                      value={quantity}
                      onChange={(e) => {
                        const raw = Number(e.target.value)
                        if (Number.isNaN(raw)) {
                          setQuantity(1)
                          return
                        }
                        const next = Math.max(1, Math.floor(raw))
                        if (outOfStockPolicy.purchaseBehavior !== 'allow_purchase' && selectionComplete) {
                          setQuantity(Math.min(next, Math.max(1, selectionStock)))
                          return
                        }
                        setQuantity(next)
                      }}
                    />
                    <button
                      type="button"
                      className="w-12 h-full flex items-center justify-center text-[#181211] hover:bg-[#f5f3f1] transition-colors"
                      onClick={() => setQuantity(prev => {
                        const next = Math.max(1, prev + 1)
                        if (outOfStockPolicy.purchaseBehavior !== 'allow_purchase') {
                          const maxQty = selectionComplete ? Math.max(1, selectionStock) : 1
                          return Math.min(next, maxQty)
                        }
                        return next
                      })}
                      disabled={outOfStockPolicy.purchaseBehavior !== 'allow_purchase' && selectionComplete && quantity >= Math.max(1, selectionStock)}
                    >
                      <span className="material-symbols-outlined text-[20px]">add</span>
                    </button>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="w-full bg-[#181211] hover:bg-black text-white py-5 rounded-lg font-bold text-sm tracking-widest uppercase transition-all flex items-center justify-center gap-3"
                onClick={handleAdd}
                disabled={buttonDisabled}
              >
                <span className="material-symbols-outlined text-xl">shopping_cart</span>
                {buttonLabel}
              </button>
              {getCartItemCount() > 0 ? (
                <button
                  type="button"
                  className="w-full border border-[#181211] text-[#181211] hover:bg-[#181211] hover:text-white py-4 rounded-lg font-bold text-sm tracking-widest uppercase transition-all flex items-center justify-center gap-3"
                  onClick={() => navigate('/carrito')}
                >
                  <span className="material-symbols-outlined text-xl">shopping_bag</span>
                  Ver carrito
                </button>
              ) : null}
            </div>

            <div className="border-t border-[#e8e6e1]">
              <details className="group" open>
                <summary className="flex justify-between items-center py-5 cursor-pointer list-none">
                  <span className="text-sm font-bold uppercase tracking-wider">Descripción</span>
                  <span className="material-symbols-outlined transition-transform group-open:rotate-180">expand_more</span>
                </summary>
                <div className="pb-6 text-sm text-gray-600 leading-relaxed">
                  {product.shortDescription ? (
                    <p className="mb-4">{product.shortDescription}</p>
                  ) : null}
                  {product.description ? (
                    <div dangerouslySetInnerHTML={{ __html: product.description }} />
                  ) : null}
                </div>
              </details>

              <details className="border-t border-[#e8e6e1] group">
                <summary className="flex justify-between items-center py-5 cursor-pointer list-none">
                  <span className="text-sm font-bold uppercase tracking-wider">Composición y Cuidados</span>
                  <span className="material-symbols-outlined transition-transform group-open:rotate-180">expand_more</span>
                </summary>
                <div className="pb-6 text-sm text-gray-600 leading-relaxed">
                  <p>{product.material || 'Sin información de composición.'}</p>
                </div>
              </details>

              <details className="border-t border-[#e8e6e1] group" id="guia-talles">
                <summary className="flex justify-between items-center py-5 cursor-pointer list-none">
                  <span className="text-sm font-bold uppercase tracking-wider">Guía de Talles</span>
                  <span className="material-symbols-outlined transition-transform group-open:rotate-180">expand_more</span>
                </summary>
                <div className="pb-6 text-sm text-gray-600 leading-relaxed">
                  {(() => {
                    const sizeGuideRaw = typeof product.sizeGuide === 'string' ? product.sizeGuide.trim() : ''
                    const sizeGuideUrl = sizeGuideRaw && (/^https?:\/\//i.test(sizeGuideRaw) ? sizeGuideRaw : (/^www\./i.test(sizeGuideRaw) ? `https://${sizeGuideRaw}` : null))
                    const sizeChart = Array.isArray(product.sizeChart) ? product.sizeChart : []
                    const rows = sizeChart
                      .map(r => ({
                        size: String(r?.size || '').trim(),
                        chest: String(r?.chest || '').trim(),
                        waist: String(r?.waist || '').trim(),
                        length: String(r?.length || '').trim()
                      }))
                      .filter(r => r.size)

                    if (!sizeGuideRaw && rows.length === 0) {
                      return <p>Este producto no tiene guía de talles cargada.</p>
                    }

                    return (
                      <div className="space-y-5">
                        {sizeGuideRaw ? (
                          sizeGuideUrl ? (
                            <div className="rounded-2xl border border-[#efe9e6] bg-[#f8f6f4] p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <div className="flex items-start gap-3">
                                <span className="material-symbols-outlined text-[#c27e70]">straighten</span>
                                <div>
                                  <div className="text-sm font-semibold text-[#181211]">Guía de talles</div>
                                  <div className="text-xs text-gray-500">Abrir guía completa en una nueva pestaña.</div>
                                </div>
                              </div>
                              <a
                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#181211] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-black transition-colors"
                                href={sizeGuideUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                                Ver guía
                              </a>
                            </div>
                          ) : (
                            <div className="rounded-2xl border border-[#efe9e6] bg-[#f8f6f4] p-4">
                              <div className="flex items-start gap-3">
                                <span className="material-symbols-outlined text-[#c27e70]">straighten</span>
                                <div className="space-y-2">
                                  <div className="text-sm font-semibold text-[#181211]">Guía de talles</div>
                                  <div className="text-sm text-gray-600 whitespace-pre-line">{sizeGuideRaw}</div>
                                </div>
                              </div>
                            </div>
                          )
                        ) : null}

                        {rows.length > 0 ? (
                          <div className="rounded-2xl border border-[#efe9e6] bg-white overflow-hidden">
                            <div className="px-4 py-3 border-b border-[#f1eeea] flex items-center gap-2">
                              <span className="material-symbols-outlined text-[#c27e70] text-[20px]">table</span>
                              <div className="text-sm font-semibold text-[#181211]">Tabla de medidas</div>
                              <div className="text-xs text-gray-500">Medidas aproximadas (según ficha).</div>
                            </div>
                            <div className="w-full overflow-auto">
                              <table className="min-w-[520px] w-full text-sm">
                                <thead>
                                  <tr className="text-left text-xs uppercase tracking-widest text-gray-500 bg-[#fbfaf9]">
                                    <th className="px-4 py-3 font-semibold">Talle</th>
                                    <th className="px-4 py-3 font-semibold">Pecho</th>
                                    <th className="px-4 py-3 font-semibold">Cintura</th>
                                    <th className="px-4 py-3 font-semibold">Largo</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map((r, index) => (
                                    <tr key={`${r.size}-${index}`} className="border-t border-[#f1eeea]">
                                      <td className="px-4 py-3 font-semibold text-[#181211]">{r.size}</td>
                                      <td className="px-4 py-3 text-gray-600">{r.chest || '-'}</td>
                                      <td className="px-4 py-3 text-gray-600">{r.waist || '-'}</td>
                                      <td className="px-4 py-3 text-gray-600">{r.length || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )
                  })()}
                </div>
              </details>

              <details className="border-t border-[#e8e6e1] group">
                <summary className="flex justify-between items-center py-5 cursor-pointer list-none">
                  <span className="text-sm font-bold uppercase tracking-wider">Envíos y Devoluciones</span>
                  <span className="material-symbols-outlined transition-transform group-open:rotate-180">expand_more</span>
                </summary>
                <div className="pb-6 text-sm text-gray-600 leading-relaxed">
                  <p>Devoluciones gratuitas en un plazo de 30 días. Envío sujeto a configuración de la tienda.</p>
                </div>
              </details>
            </div>
          </div>
        </div>
      </div>

      <section className="w-full max-w-[1440px] px-6 lg:px-12 py-24 border-t border-[#e8e6e1]">
        <h2 className="text-2xl font-bold tracking-tight mb-10">También te puede interesar</h2>
        {relatedProducts.length === 0 ? (
          <div className="text-sm text-gray-500">Sin recomendaciones por el momento.</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {relatedProducts.map((p) => {
              const relatedImages = getVariantImages(p, { width: 800, height: 1000, crop: 'fill' })
              const img = relatedImages[0] || getImageUrl(null)
              const hasDiscount2 = p.specialPrice > 0 && p.specialPrice < p.price
              const price2 = hasDiscount2 ? p.specialPrice : p.price
              return (
                <Link key={p.id} to={`/productos/${p.id}`} className="group flex flex-col gap-3">
                  <div className="relative w-full aspect-[3/4] bg-[#f4f3f1] rounded-lg overflow-hidden">
                    <img alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" src={img} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <h3 className="font-bold text-sm leading-tight group-hover:text-[#c27e70] transition-colors">{p.name}</h3>
                    <span className="text-sm font-medium text-gray-500">{formatCurrency(price2 || 0)}</span>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </section>
    </main>
  )
}

export default FashionProductDetail
